import React, { useState, useCallback } from 'react';

interface IniSection {
  name: string;
  properties: Record<string, string>;
  comments: string[];
}

interface ParsedIni {
  sections: IniSection[];
  globalProperties: Record<string, string>;
  globalComments: string[];
}

interface IniFormatterProps {
  initialValue?: string;
  onFormatted?: (formatted: string) => void;
  className?: string;
}

export class IniParser {
  static parse(content: string): ParsedIni {
    const lines = content.split('\n');
    const result: ParsedIni = {
      sections: [],
      globalProperties: {},
      globalComments: []
    };

    let currentSection: IniSection | null = null;
    let pendingComments: string[] = [];

    for (let line of lines) {
      const trimmed = line.trim();
      
      // Skip empty lines
      if (!trimmed) {
        continue;
      }

      // Handle comments
      if (trimmed.startsWith(';') || trimmed.startsWith('#')) {
        pendingComments.push(trimmed);
        continue;
      }

      // Handle section headers
      if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
        const sectionName = trimmed.slice(1, -1).trim();
        currentSection = {
          name: sectionName,
          properties: {},
          comments: [...pendingComments]
        };
        result.sections.push(currentSection);
        pendingComments = [];
        continue;
      }

      // Handle key-value pairs
      const equalIndex = trimmed.indexOf('=');
      if (equalIndex > 0) {
        const key = trimmed.slice(0, equalIndex).trim();
        const value = trimmed.slice(equalIndex + 1).trim();
        
        if (currentSection) {
          currentSection.properties[key] = value;
        } else {
          result.globalProperties[key] = value;
        }
        
        // Assign pending comments to global if no section
        if (!currentSection && pendingComments.length > 0) {
          result.globalComments.push(...pendingComments);
          pendingComments = [];
        }
      }
    }

    // Handle any remaining comments
    if (pendingComments.length > 0) {
      if (currentSection) {
        currentSection.comments.push(...pendingComments);
      } else {
        result.globalComments.push(...pendingComments);
      }
    }

    return result;
  }

  static format(parsed: ParsedIni, options: { 
    indentSize?: number; 
    sortSections?: boolean; 
    sortProperties?: boolean;
    preserveComments?: boolean;
  } = {}): string {
    const { 
      indentSize = 2, 
      sortSections = false, 
      sortProperties = false,
      preserveComments = true 
    } = options;
    
    const indent = ' '.repeat(indentSize);
    const lines: string[] = [];

    // Add global comments
    if (preserveComments && parsed.globalComments.length > 0) {
      lines.push(...parsed.globalComments);
      lines.push('');
    }

    // Add global properties
    const globalKeys = sortProperties 
      ? Object.keys(parsed.globalProperties).sort()
      : Object.keys(parsed.globalProperties);
    
    for (const key of globalKeys) {
      lines.push(`${key} = ${parsed.globalProperties[key]}`);
    }

    if (globalKeys.length > 0) {
      lines.push('');
    }

    // Add sections
    const sections = sortSections 
      ? [...parsed.sections].sort((a, b) => a.name.localeCompare(b.name))
      : parsed.sections;

    for (let i = 0; i < sections.length; i++) {
      const section = sections[i];
      
      // Add section comments
      if (preserveComments && section.comments.length > 0) {
        lines.push(...section.comments);
      }

      // Add section header
      lines.push(`[${section.name}]`);

      // Add section properties
      const propertyKeys = sortProperties 
        ? Object.keys(section.properties).sort()
        : Object.keys(section.properties);

      for (const key of propertyKeys) {
        lines.push(`${indent}${key} = ${section.properties[key]}`);
      }

      // Add spacing between sections (except for the last one)
      if (i < sections.length - 1) {
        lines.push('');
      }
    }

    return lines.join('\n');
  }
}

export const IniFormatter: React.FC<IniFormatterProps> = ({
  initialValue = '',
  onFormatted,
  className = ''
}) => {
  const [input, setInput] = useState(initialValue);
  const [output, setOutput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [options, setOptions] = useState({
    indentSize: 2,
    sortSections: false,
    sortProperties: false,
    preserveComments: true
  });

  const formatIni = useCallback(() => {
    try {
      setError(null);
      const parsed = IniParser.parse(input);
      const formatted = IniParser.format(parsed, options);
      setOutput(formatted);
      onFormatted?.(formatted);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      setOutput('');
    }
  }, [input, options, onFormatted]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
  };

  const handleOptionChange = (key: keyof typeof options, value: any) => {
    setOptions(prev => ({ ...prev, [key]: value }));
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(output);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const validateIni = () => {
    try {
      IniParser.parse(input);
      setError(null);
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Invalid INI format';
      setError(errorMessage);
      return false;
    }
  };

  return (
    <div className={`ini-formatter ${className}`}>
      <div className="ini-formatter__header">
        <h2>INI Formatter</h2>
        <div className="ini-formatter__options">
          <label>
            <input
              type="number"
              min="0"
              max="8"
              value={options.indentSize}
              onChange={(e) => handleOptionChange('indentSize', parseInt(e.target.value))}
            />
            Indent Size
          </label>
          <label>
            <input
              type="checkbox"
              checked={options.sortSections}
              onChange={(e) => handleOptionChange('sortSections', e.target.checked)}
            />
            Sort Sections
          </label>
          <label>
            <input
              type="checkbox"
              checked={options.sortProperties}
              onChange={(e) => handleOptionChange('sortProperties', e.target.checked)}
            />
            Sort Properties
          </label>
          <label>
            <input
              type="checkbox"
              checked={options.preserveComments}
              onChange={(e) => handleOptionChange('preserveComments', e.target.checked)}
            />
            Preserve Comments
          </label>
        </div>
      </div>

      <div className="ini-formatter__content">
        <div className="ini-formatter__input-section">
          <div className="ini-formatter__section-header">
            <label htmlFor="ini-input">Input INI</label>
            <div className="ini-formatter__actions">
              <button onClick={validateIni} className="btn btn--secondary">
                Validate
              </button>
              <button onClick={formatIni} className="btn btn--primary">
                Format
              </button>
            </div>
          </div>
          <textarea
            id="ini-input"
            value={input}
            onChange={handleInputChange}
            placeholder="Paste your INI content here..."
            className="ini-formatter__textarea"
            rows={15}
          />
        </div>

        <div className="ini-formatter__output-section">
          <div className="ini-formatter__section-header">
            <label htmlFor="ini-output">Formatted Output</label>
            {output && (
              <button onClick={copyToClipboard} className="btn btn--secondary">
                Copy
              </button>
            )}
          </div>
          {error ? (
            <div className="ini-formatter__error">
              <strong>Error:</strong> {error}
            </div>
          ) : (
            <textarea
              id="ini-output"
              value={output}
              readOnly
              className="ini-formatter__textarea ini-formatter__textarea--readonly"
              rows={15}
            />
          )}
        </div>
      </div>

      <style>{`
        .ini-formatter {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
          font-family: 'SF Pro Text', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .ini-formatter__header {
          margin-bottom: 24px;
        }

        .ini-formatter__header h2 {
          margin: 0 0 16px 0;
          color: #1a1a1a;
          font-size: 24px;
          font-weight: 600;
        }

        .ini-formatter__options {
          display: flex;
          gap: 20px;
          flex-wrap: wrap;
        }

        .ini-formatter__options label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          color: #666;
        }

        .ini-formatter__options input[type="number"] {
          width: 60px;
          padding: 4px 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
        }

        .ini-formatter__options input[type="checkbox"] {
          width: 16px;
          height: 16px;
        }

        .ini-formatter__content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
        }

        .ini-formatter__section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .ini-formatter__section-header label {
          font-weight: 600;
          color: #333;
          font-size: 16px;
        }

        .ini-formatter__actions {
          display: flex;
          gap: 8px;
        }

        .btn {
          padding: 8px 16px;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn--primary {
          background: #007bff;
          color: white;
        }

        .btn--primary:hover {
          background: #0056b3;
        }

        .btn--secondary {
          background: #f8f9fa;
          color: #6c757d;
          border: 1px solid #dee2e6;
        }

        .btn--secondary:hover {
          background: #e9ecef;
        }

        .ini-formatter__textarea {
          width: 100%;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
          font-size: 14px;
          line-height: 1.5;
          resize: vertical;
          background: #fafafa;
        }

        .ini-formatter__textarea:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
        }

        .ini-formatter__textarea--readonly {
          background: #f8f9fa;
          color: #495057;
        }

        .ini-formatter__error {
          padding: 12px;
          background: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
          border-radius: 8px;
          font-size: 14px;
        }

        @media (max-width: 768px) {
          .ini-formatter__content {
            grid-template-columns: 1fr;
          }
          
          .ini-formatter__options {
            flex-direction: column;
            gap: 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default IniFormatter;